<template>
    <div class="box">
        <table
            v-if="library"
            class="table is-striped is-fullwidth">
            <tbody>
                <tr>
                    <td colspan="2">
                        <type-icon-row
                            size="is-medium"
                            tooltip-position="is-right"
                            :library="library" />
                    </td>
                </tr>
                <tr>
                    <th>Description</th>
                    <td colspan="2">
                        {{ library.description }}
                    </td>
                </tr>

                <tr>
                    <th>Alias</th>
                    <td>{{ library.alias }}</td>
                    <td class="helpCol">
                        <help-icon text="The library may also be known by this name" />
                    </td>
                </tr>
                <tr>
                    <th>Scopetype</th>
                    <td>
                        <type-icon
                            v-show="library.isGlobal"
                            type="global" />
                        <type-icon
                            v-show="library.scopeType == 'Facility'"
                            type="facility" />
                        {{ scopeDescription }}
                    </td>
                    <td class="helpCol">
                        <help-icon>
                            <template #helpContent>
                                A library may be global, or it may be scoped by some other library.<br>
                                "Country" is an example of a global library, as its values will be consistent for all facilities.<br>
                                "Location" is an example of a scoped library, as its values will be different for different facilities.
                            </template>
                        </help-icon>
                    </td>
                </tr>
                <tr v-show="!library.isGlobal && !library.isRelation">
                    <th>Is scope specific</th>
                    <td>
                        <bool-element
                            :value="library.isScopeSpecific"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon>
                            <template #helpContent>
                                The values in the library sets for this library are purely scope specific,<br>
                                meaning its content should not be copied when introducing new scopes.
                            </template>
                        </help-icon>
                    </td>
                </tr>
                <tr v-show="!library.isRelation">
                    <th>Is case sensitive</th>
                    <td>
                        <bool-element
                            :value="library.isCaseSensitive"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon>
                            <template #helpContent>
                                The Name of items in this library will be case sensitive.<br>
                                <i>ABC</i>, <i>Abc</i>, and <i>abc</i> can coexist as distinct values.
                            </template>
                        </help-icon>
                    </td>
                </tr>
                <tr>
                    <th>Is read only</th>
                    <td>
                        <bool-element
                            :value="library.isReadOnly"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon>
                            <template #helpContent>
                                The data is not maintained in Common Library.<br>
                                As a user you will not be able to change library items.
                            </template>
                        </help-icon>
                    </td>
                </tr>
                <tr v-show="!library.isRelation">
                    <th>Name may be changed</th>
                    <td>
                        <bool-element
                            :value="library.nameMayChange"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon text="The Name of items can be changed. Can only be set if Name is not part of identity." />
                    </td>
                </tr>
                <tr>
                    <th>Names must be unique</th>
                    <td>
                        <bool-element
                            :value="library.nameMustBeUnique"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon>
                            <template #helpContent>
                                The names of valid items must be unique across a codeset.<br>
                                If the identity of an item consists only of its name, uniqueness is enforced even when this is not set
                            </template>
                        </help-icon>
                    </td>
                </tr>
                <tr v-show="!library.isRelation">
                    <th>Uppercase names</th>
                    <td>
                        <bool-element
                            :value="library.areNamesUpperCase"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon text="All names will always be uppercase." />
                    </td>
                </tr>
                <tr v-show="library.isRelation">
                    <th>Is relation</th>
                    <td>
                        <bool-element
                            :value="library.isRelation"
                            :has-yes-no="true" />
                    </td>
                    <td class="helpCol">
                        <help-icon text="Is relation between libraries." />
                    </td>
                </tr>
                <tr>
                    <th>Date created</th>
                    <td colspan="2">
                        {{ shortDate(library.dateCreated) }}
                    </td>
                </tr>
                <tr>
                    <th>Date updated</th>
                    <td colspan="2">
                        {{ shortDate(library.dateUpdated) }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
    import BoolElement from '@/shared/components/BoolElement.vue';
    import HelpIcon from '@/shared/components/HelpIcon.vue';
    import TypeIcon from '@/shared/components/TypeIcon.vue';
    import TypeIconRow from '@/shared/components/TypeIconRow.vue';
    import { shortDate } from '@/formatters.ts';

    export default {
        components: {
            BoolElement,
            HelpIcon,
            TypeIcon,
            TypeIconRow
        },
        props: {
            library: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
            };
        },
        computed: {
            scopeDescription() {
                if (this.library.isGlobal)
                    return 'Global library';
                if (this.library.scopeType === null || this.library.scopeType === '')
                    return 'Scoping has not been configured for this library';
                return `Scoped by ${this.library.scopeType}`;
            }
        },
        methods: { shortDate }
    };
</script>

<style scoped>
    .helpCol {
        padding-right: 1px;
        padding-left: 2px;
    }
</style>
